import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Trophy, Megaphone, Gift, ShieldCheck, ArrowUpLeft, Link2, Check } from 'lucide-react';
import { toast } from '../lib/toast';
import { resolveLogoFullId } from '../lib/platformLogo';

const API = process.env.REACT_APP_BACKEND_URL;
const ICONS = { sparkles: Sparkles, trophy: Trophy, megaphone: Megaphone, gift: Gift, shield: ShieldCheck };

const AUDIENCE_FILTERS = [
  { key: 'all', label: 'الكل' },
  { key: 'clubs', label: 'الأندية' },
  { key: 'academies', label: 'الأكاديميات' },
  { key: 'coaches', label: 'المدربون' },
];

const PRIORITY_LABEL = { high: 'مهم', urgent: 'عاجل' };

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('ar-SA', { day: 'numeric', month: 'long', year: 'numeric' }); } catch { return ''; }
};

function UpdateCard({ u }) {
  const CardIcon = ICONS[u.icon] || Sparkles;
  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/updates#${u.id}`;

  const copyLink = () => {
    navigator.clipboard?.writeText(shareUrl).then(() => {
      setCopied(true);
      toast.success('تم نسخ رابط التحديث');
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => toast.error('تعذّر نسخ الرابط'));
  };

  return (
    <article id={u.id} className="overflow-hidden rounded-2xl border border-white/10 bg-[#0f2438] shadow-lg" data-testid={`update-card-${u.id}`}>
      {u.banner_image_url && (
        <div className="relative h-48 w-full sm:h-64">
          <img src={`${API}${u.banner_image_url}`} alt={u.title} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0f2438] via-transparent to-transparent" />
        </div>
      )}
      <div className="p-6 sm:p-8">
        <div className="flex flex-wrap items-center gap-2 text-xs text-white/50">
          <span data-testid={`update-date-${u.id}`}>{fmtDate(u.publish_date)}</span>
          {PRIORITY_LABEL[u.priority] && (
            <span className="rounded-full bg-red-500/15 px-2.5 py-0.5 font-bold text-red-300">{PRIORITY_LABEL[u.priority]}</span>
          )}
        </div>

        <div className="mt-4 flex items-start gap-3.5">
          {u.image_url ? (
            <img src={`${API}${u.image_url}`} alt="" className="h-12 w-12 shrink-0 rounded-xl object-cover" />
          ) : (
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-teal-500/20 text-teal-300">
              <CardIcon className="h-6 w-6" />
            </div>
          )}
          <div className="min-w-0">
            <span className="rounded-full bg-orange-500/20 border border-orange-400/30 px-2.5 py-1 text-[11px] font-bold text-orange-300">{u.badge || 'جديد'}</span>
            <h2 className="mt-2 text-xl font-bold text-white sm:text-2xl" data-testid={`update-title-${u.id}`}>{u.title}</h2>
          </div>
        </div>

        <p className="mt-4 whitespace-pre-line text-[15px] leading-8 text-white/80" data-testid={`update-description-${u.id}`}>{u.description}</p>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-5">
          {u.link ? (
            <a
              href={u.link}
              target={u.link.startsWith('/') ? undefined : '_blank'}
              rel={u.link.startsWith('/') ? undefined : 'noopener noreferrer'}
              className="inline-flex items-center gap-1.5 rounded-full bg-teal-400 px-4 py-2 text-sm font-bold text-[#0f2438] transition-colors hover:bg-teal-300"
              data-testid={`update-link-${u.id}`}
            >
              اكتشف الميزة <ArrowUpLeft className="h-4 w-4" />
            </a>
          ) : <span />}
          <div className="flex items-center gap-2">
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`${u.title} — ${shareUrl}`)}`}
              target="_blank" rel="noreferrer"
              title="مشاركة عبر واتساب"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-white/60 transition-colors hover:border-teal-400/50 hover:text-teal-300"
              data-testid={`update-share-whatsapp-${u.id}`}
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.87.5 3.62 1.44 5.15L2 22l5.1-1.53a9.87 9.87 0 0 0 4.94 1.32h.01c5.46 0 9.9-4.45 9.9-9.91C21.95 6.45 17.5 2 12.04 2Zm0 18.06h-.01a8.2 8.2 0 0 1-4.18-1.14l-.3-.18-3.1.93.93-3.02-.2-.31a8.13 8.13 0 0 1-1.25-4.33c0-4.5 3.66-8.17 8.16-8.17 4.5 0 8.16 3.66 8.16 8.16 0 4.5-3.66 8.16-8.21 8.16Zm4.48-6.1c-.24-.12-1.43-.71-1.65-.79-.22-.08-.38-.12-.55.12-.16.24-.63.79-.77.95-.14.16-.28.18-.53.06-.24-.12-1.02-.38-1.94-1.2-.72-.64-1.2-1.43-1.34-1.67-.14-.24-.02-.37.1-.5.12-.12.28-.32.42-.48.14-.16.19-.28.28-.46.09-.19.05-.35-.03-.48-.08-.13-.5-1.2-.69-1.65-.18-.44-.37-.38-.51-.39h-.44c-.14 0-.38.05-.58.28-.2.24-.77.75-.77 1.83s.79 2.13.9 2.28c.12.15 1.61 2.46 3.9 3.35 2.29.89 2.29.6 2.7.56.41-.04 1.34-.55 1.53-1.08.19-.53.19-.98.13-1.08-.06-.1-.24-.16-.48-.28Z"/></svg>
            </a>
            <button
              onClick={copyLink}
              title="نسخ رابط التحديث"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-white/60 transition-colors hover:border-teal-400/50 hover:text-teal-300"
              data-testid={`update-copy-link-${u.id}`}
            >
              {copied ? <Check className="h-4 w-4 text-teal-300" /> : <Link2 className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function UpdatesPage() {
  const navigate = useNavigate();
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [audience, setAudience] = useState('all');
  const [platformSettings, setPlatformSettings] = useState({});

  useEffect(() => {
    axios.get(`${API}/api/platform/updates/public?limit=100`).then((res) => {
      setUpdates(Array.isArray(res.data) ? res.data : []);
    }).catch(() => setUpdates([])).finally(() => setLoading(false));
    axios.get(`${API}/api/platform/settings`).then((res) => setPlatformSettings(res.data || {})).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (audience === 'all') return updates;
    return updates.filter((u) => u.audience === 'all' || u.audience === audience);
  }, [updates, audience]);

  const darkLogoId = resolveLogoFullId(platformSettings, 'dark');

  return (
    <div dir="rtl" className="min-h-screen bg-[#040a12]" data-testid="updates-page">
      <header className="sticky top-0 z-20 border-b border-white/10 bg-[#040a12]/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4">
          <button onClick={() => navigate('/')} className="text-sm font-semibold text-teal-300 hover:underline" data-testid="updates-back-btn">← الرئيسية</button>
          {darkLogoId ? (
            <img src={`${API}/api/files/${darkLogoId}`} alt={platformSettings.platform_name || 'تسيار'} className="h-6 w-auto object-contain" />
          ) : (
            <span className="font-display text-lg font-black tracking-tight">
              <span className="text-white">تس</span><span className="text-teal-400">يار</span>
            </span>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-10">
        <div className="mb-8 flex items-center gap-2.5">
          <span className="h-2.5 w-2.5 rounded-full bg-teal-400 shadow-[0_0_10px_3px_rgba(45,212,191,0.5)]" />
          <h1 className="text-2xl font-bold text-white sm:text-3xl" data-testid="updates-page-title">آخر تحديثات تسيار</h1>
        </div>
        <p className="mb-6 text-sm text-white/50">كل جديد نطوّره في المنصة — ميزات، تحسينات، وإصلاحات — في مكان واحد</p>

        <div className="mb-8 flex flex-wrap gap-2" data-testid="updates-audience-filters">
          {AUDIENCE_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setAudience(f.key)}
              className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${audience === f.key ? 'border-teal-400 bg-teal-400/15 text-teal-300' : 'border-white/10 text-white/50 hover:border-white/25 hover:text-white/80'}`}
              data-testid={`updates-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => <div key={i} className="h-40 animate-pulse rounded-2xl bg-white/5" />)}
          </div>
        ) : filtered.length === 0 ? (
          <p className="py-16 text-center text-sm text-white/40" data-testid="updates-empty-state">لا توجد تحديثات لعرضها حالياً</p>
        ) : (
          <div className="space-y-6">
            {filtered.map((u) => <UpdateCard key={u.id} u={u} />)}
          </div>
        )}
      </main>
    </div>
  );
}
